import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchWompiStatus,
  removeWompiConfig,
  saveWompiConfig,
  testWompiConnection,
} from '@/api/wompi';
import { queryKeys } from '@/lib/query-keys';
import type { SaveWompiPayload } from '@/types/integrations';

/**
 * Estado de Wompi del negocio activo (pago en línea, docs/WOMPI.md).
 *
 * El estado lo lee cualquier miembro; guardar, quitar y probar es solo ADMIN (el backend lo
 * bloquea igual con `requireRole('ADMIN')`; esto es para no pintar botones que no van a funcionar).
 * Toda mutación invalida `['tenants', tenantId, 'wompi']`.
 */

/** `GET /api/tenants/:tenantId/wompi`. */
export function useWompiStatus(tenantId: string | null) {
  return useQuery({
    queryKey: queryKeys.wompi(tenantId ?? ''),
    queryFn: () => fetchWompiStatus(tenantId ?? ''),
    enabled: Boolean(tenantId),
  });
}

/** `PUT .../wompi` (solo ADMIN). El backend hace merge de lo que no venga. */
export function useSaveWompiConfig(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SaveWompiPayload) => saveWompiConfig(tenantId ?? '', payload),
    onSuccess: () => {
      if (tenantId) queryClient.invalidateQueries({ queryKey: queryKeys.wompi(tenantId) });
    },
  });
}

/** `DELETE .../wompi` (solo ADMIN) — borra la config y las llaves. */
export function useRemoveWompiConfig(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => removeWompiConfig(tenantId ?? ''),
    onSuccess: () => {
      if (tenantId) queryClient.invalidateQueries({ queryKey: queryKeys.wompi(tenantId) });
    },
  });
}

/** `POST .../wompi/test` (solo ADMIN). Siempre 200: el veredicto va en `ok`. */
export function useTestWompiConnection(tenantId: string | null) {
  return useMutation({
    mutationFn: (payload?: { llavePublica?: string; ambiente?: SaveWompiPayload['ambiente'] }) =>
      testWompiConnection(tenantId ?? '', payload),
  });
}